import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import "../../Styles/EventsForm.css";
import { eventsAPI, fileAPI } from "../../api/index"; // adjust path to match your project

// Shared mandal id -> name lookup. CONFIRM these ids match your actual
// `mandals` table rows — these are placeholders in dropdown order.
export const MANDALS = [
  { id: 1, name: "Kandukur" },
  { id: 2, name: "Lingasamudram" },
  { id: 3, name: "Gudluru" },
  { id: 4, name: "Ulavapadu" },
  { id: 5, name: "Voletivaripalem" },
];

const EVENT_TYPES = [
  "Inauguration",
  "Public Meeting",
  "Review Meeting",
  "Felicitation",
  "Grievance Camp",
  "Cultural Event",
  "Other",
];
const STATUSES = ["Upcoming", "Completed", "Cancelled"];

const MAX_PHOTO_SIZE = 5 * 1024 * 1024; // 5MB
const ALLOWED_IMAGE_TYPES = ["image/jpeg", "image/png", "image/webp"];

const INITIAL_FORM = {
  title: "",
  type: "",
  venue: "",
  guest: "",
  village: "",
  mandalId: "",
  date: "",
  time: "",
  attendance: "",
  status: "",
  description: "",
};

// Order used to focus the first invalid field on submit (same as the form layout)
const FIELD_ORDER = [
  "title",
  "type",
  "venue",
  "guest",
  "village",
  "mandalId",
  "date",
  "time",
  "attendance",
  "status",
  "description",
];

// These fields show a format error immediately while typing
const LIVE_VALIDATE_FIELDS = ["village", "attendance"];

const TITLE_REGEX = /^[A-Za-z0-9\s.,\-&'()/:]+$/;
const GUEST_REGEX = /^[A-Za-z\s.,&'\-]+$/;
const VILLAGE_REGEX = /^[A-Za-z\s]+$/;
const TIME_REGEX = /^([01]\d|2[0-3]):[0-5]\d$/;

// ---------- Field validation rules ----------
const validateField = (name, value) => {
  const v = typeof value === "string" ? value.trim() : value;

  switch (name) {
    case "title":
      if (!v) return "Event title is required";
      if (v.length < 3) return "Event title must be at least 3 characters";
      if (v.length > 150) return "Event title must not exceed 150 characters";
      if (!TITLE_REGEX.test(v))
        return "Title can contain only letters, numbers, spaces and . , - & ' ( ) / :";
      return "";

    case "type":
      if (!v) return "Please select an Event Type";
      if (!EVENT_TYPES.includes(v)) return "Please select a valid Event Type";
      return "";

    case "venue":
      if (!v) return "Venue is required";
      if (v.length < 3) return "Venue must be at least 3 characters";
      if (v.length > 150) return "Venue must not exceed 150 characters";
      return "";

    case "guest":
      if (!v) return ""; // optional
      if (v.length < 3) return "Name must be at least 3 characters";
      if (v.length > 100) return "Name must not exceed 100 characters";
      if (!GUEST_REGEX.test(v))
        return "Name can contain only alphabets, spaces and . , & ' -";
      return "";

    case "village":
      if (!v) return "Village is required";
      if (!VILLAGE_REGEX.test(v)) return "Village name can contain only alphabets (A-Z)";
      if (v.length < 2) return "Village name must be at least 2 characters";
      if (v.length > 100) return "Village name must not exceed 100 characters";
      return "";

    case "mandalId":
      if (!v) return "Please select a Mandal";
      if (!MANDALS.some((m) => String(m.id) === String(v)))
        return "Please select a valid Mandal";
      return "";

    case "date": {
      if (!v) return "Date is required";
      const d = new Date(v);
      if (Number.isNaN(d.getTime())) return "Enter a valid date";
      if (d.getFullYear() < 1990) return "Date cannot be before 1990";
      if (d.getFullYear() > 2100) return "Date cannot be after 2100";
      return "";
    }

    case "time":
      if (!v) return "Time is required";
      if (!TIME_REGEX.test(v)) return "Enter a valid time";
      return "";

    case "attendance": {
      if (!v) return ""; // optional
      if (!/^\d+$/.test(v)) return "Expected attendance must be a whole number";
      const n = Number(v);
      if (n < 1) return "Expected attendance must be at least 1";
      if (n > 1000000) return "Expected attendance is too large";
      return "";
    }

    case "status":
      if (!v) return "Please select a Status";
      if (!STATUSES.includes(v)) return "Please select a valid Status";
      return "";

    case "description":
      if (!v) return ""; // optional
      if (v.length < 10) return "Description must be at least 10 characters";
      if (v.length > 1000) return "Description must not exceed 1000 characters";
      return "";

    default:
      return "";
  }
};

const validateAll = (data) => {
  const errors = {};
  FIELD_ORDER.forEach((name) => {
    const msg = validateField(name, data[name]);
    if (msg) errors[name] = msg;
  });
  return errors;
};

const EventsForm = () => {
  const navigate = useNavigate();
  const photoInputRef = useRef(null);

  const [formData, setFormData] = useState(INITIAL_FORM);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});

  const [photo, setPhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState("");
  const [photoError, setPhotoError] = useState("");

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(""); // server / API errors only

  const showError = (name) => touched[name] && errors[name];
  const inputClass = (name) => (showError(name) ? "input-error" : "");

  const handleChange = (e) => {
    const { name } = e.target;
    let { value } = e.target;

    // Expected attendance: digits only
    if (name === "attendance") value = value.replace(/\D/g, "");

    setFormData((prev) => ({ ...prev, [name]: value }));

    const isLive = LIVE_VALIDATE_FIELDS.includes(name);
    if (touched[name] || isLive) {
      if (isLive && !touched[name]) {
        setTouched((prev) => ({ ...prev, [name]: true }));
      }
      setErrors((prev) => ({ ...prev, [name]: validateField(name, value) }));
    }
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));
    setErrors((prev) => ({ ...prev, [name]: validateField(name, value) }));
  };

  /* ---------- Photo (optional) ---------- */
  const clearPhoto = () => {
    if (photoPreview) URL.revokeObjectURL(photoPreview);
    setPhoto(null);
    setPhotoPreview("");
    if (photoInputRef.current) photoInputRef.current.value = "";
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files?.[0];

    if (!file) {
      clearPhoto();
      setPhotoError("");
      return;
    }
    if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
      clearPhoto();
      setPhotoError("Only JPG, PNG or WEBP images are allowed");
      return;
    }
    if (file.size > MAX_PHOTO_SIZE) {
      clearPhoto();
      setPhotoError("Photo must not exceed 5MB");
      return;
    }

    if (photoPreview) URL.revokeObjectURL(photoPreview);
    setPhoto(file);
    setPhotoPreview(URL.createObjectURL(file));
    setPhotoError("");
  };

  const handleReset = () => {
    setFormData(INITIAL_FORM);
    setErrors({});
    setTouched({});
    clearPhoto();
    setPhotoError("");
    setError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    // Validate every field and mark all as touched so every error is shown
    const newErrors = validateAll(formData);
    setErrors(newErrors);
    setTouched(FIELD_ORDER.reduce((acc, k) => ({ ...acc, [k]: true }), {}));

    const firstInvalid = FIELD_ORDER.find((f) => newErrors[f]);
    if (firstInvalid || photoError) {
      toast.error("Please fix the highlighted fields");
      const target = firstInvalid ? firstInvalid : "photo";
      const el = document.querySelector(`[name="${target}"]`);
      if (el) el.focus();
      return;
    }

    setSubmitting(true);
    try {
      let photoUrl = null;
      if (photo) {
        const uploadRes = await fileAPI.upload(photo, "events");
        photoUrl =
          uploadRes.data?.url ||
          uploadRes.data?.file_url ||
          uploadRes.data?.photo_url;
      }

      const mandalName = MANDALS.find(
        (m) => String(m.id) === String(formData.mandalId)
      )?.name;

      await eventsAPI.create({
        event_title: formData.title.trim(),
        event_type: formData.type,
        venue: formData.venue.trim(),
        chief_guest: formData.guest.trim() || null,
        village_name: formData.village.trim(),
        mandal_name: mandalName, // schema wants a name; we send the resolved name from the chosen id
        event_date: formData.date, // <input type="date"> already gives YYYY-MM-DD
        event_time: formData.time ? `${formData.time}:00` : null, // <input type="time"> gives HH:MM, backend wants HH:MM:SS
        expected_attendance: formData.attendance ? Number(formData.attendance) : null,
        status: formData.status.toLowerCase(),
        description: formData.description.trim() || null,
        photo_url: photoUrl,
      });

      // Success toast — stays visible after navigating to the table page
      toast.success("Form submitted successfully", { toastId: "event-form-success" });

      handleReset();
      navigate("/dashboard/EventsTable");
    } catch (err) {
      console.error("Event save failed:", err);
      setError(
        err.response?.data?.detail || err.message || "Something went wrong while saving the event."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="event-form-container">
      <div className="event-form-card">
        <div className="event-form-header">
          <h2>Add New Event</h2>
          <p>Schedule public meetings, inaugurations, and community events</p>
          <button
            type="button"
            className="event-back-btn"
            onClick={() => navigate(-1)}
          >
            Back
          </button>
        </div>

        {error && <div className="event-form-error">{error}</div>}

        {/* noValidate -> disables browser default popups so our custom messages show */}
        <form onSubmit={handleSubmit} noValidate>
          <div className="event-form-grid">

            <div className="event-form-group">
              <label>Event Title *</label>
              <input
                type="text"
                name="title"
                placeholder="Enter Event Title"
                value={formData.title}
                onChange={handleChange}
                onBlur={handleBlur}
                className={inputClass("title")}
                maxLength={150}
              />
              {showError("title") && <span className="error-text">{errors.title}</span>}
            </div>

            <div className="event-form-group">
              <label>Event Type *</label>
              <select
                name="type"
                value={formData.type}
                onChange={handleChange}
                onBlur={handleBlur}
                className={inputClass("type")}
              >
                <option value="">Select Type</option>
                {EVENT_TYPES.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
              {showError("type") && <span className="error-text">{errors.type}</span>}
            </div>

            <div className="event-form-group">
              <label>Venue *</label>
              <input
                type="text"
                name="venue"
                placeholder="Enter Venue"
                value={formData.venue}
                onChange={handleChange}
                onBlur={handleBlur}
                className={inputClass("venue")}
                maxLength={150}
              />
              {showError("venue") && <span className="error-text">{errors.venue}</span>}
            </div>

            <div className="event-form-group">
              <label>Chief Guest / Organizer (Optional)</label>
              <input
                type="text"
                name="guest"
                placeholder="Enter Name"
                value={formData.guest}
                onChange={handleChange}
                onBlur={handleBlur}
                className={inputClass("guest")}
                maxLength={100}
              />
              {showError("guest") && <span className="error-text">{errors.guest}</span>}
            </div>

           
            <div className="event-form-group">
              <label>Mandal *</label>
              <select
                name="mandalId"
                value={formData.mandalId}
                onChange={handleChange}
                onBlur={handleBlur}
                className={inputClass("mandalId")}
              >
                <option value="">Choose Mandal</option>
                {MANDALS.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.name}
                  </option>
                ))}
              </select>
              {showError("mandalId") && <span className="error-text">{errors.mandalId}</span>}
            </div>
             <div className="event-form-group">
              <label>Village *</label>
              <input
                type="text"
                name="village"
                placeholder="Enter Village (alphabets only)"
                value={formData.village}
                onChange={handleChange}
                onBlur={handleBlur}
                className={inputClass("village")}
                maxLength={100}
              />
              {showError("village") && <span className="error-text">{errors.village}</span>}
            </div>


            <div className="event-form-group">
              <label>Date *</label>
              <input
                type="date"
                name="date"
                value={formData.date}
                onChange={handleChange}
                onBlur={handleBlur}
                className={inputClass("date")}
                min="1990-01-01"
                max="2100-12-31"
              />
              {showError("date") && <span className="error-text">{errors.date}</span>}
            </div>

            <div className="event-form-group">
              <label>Time *</label>
              <input
                type="time"
                name="time"
                value={formData.time}
                onChange={handleChange}
                onBlur={handleBlur}
                className={inputClass("time")}
              />
              {showError("time") && <span className="error-text">{errors.time}</span>}
            </div>

            <div className="event-form-group">
              <label>Expected Attendance (Optional)</label>
              {/* type="text" + inputMode="numeric" so we can show our own error message */}
              <input
                type="text"
                inputMode="numeric"
                name="attendance"
                placeholder="Enter Expected Attendance"
                value={formData.attendance}
                onChange={handleChange}
                onBlur={handleBlur}
                className={inputClass("attendance")}
                maxLength={7}
              />
              {showError("attendance") && <span className="error-text">{errors.attendance}</span>}
            </div>

            <div className="event-form-group">
              <label>Status *</label>
              <select
                name="status"
                value={formData.status}
                onChange={handleChange}
                onBlur={handleBlur}
                className={inputClass("status")}
              >
                <option value="">Select Status</option>
                {STATUSES.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
              {showError("status") && <span className="error-text">{errors.status}</span>}
            </div>

            <div className="event-form-group">
              <label>Upload Event Photo (Optional)</label>
              <input
                ref={photoInputRef}
                type="file"
                name="photo"
                accept="image/jpeg,image/png,image/webp"
                onChange={handlePhotoChange}
                className={photoError ? "input-error" : ""}
              />
              {photoError && <span className="error-text">{photoError}</span>}
              <small style={{ color: "#9ca3af", fontSize: "12px", marginTop: "4px", display: "block" }}>
                JPG, PNG, WEBP (Max 5MB)
              </small>

              {photoPreview && (
                <div className="photo-preview-wrapper">
                  <img src={photoPreview} alt="Selected" className="photo-preview-img" />
                  <button
                    type="button"
                    className="photo-remove-btn"
                    onClick={clearPhoto}
                    aria-label="Remove photo"
                  >
                    ×
                  </button>
                </div>
              )}
            </div>

          </div>

          <div className="event-form-group event-full-width">
            <label>Event Description (Optional)</label>
            <textarea
              rows="4"
              name="description"
              placeholder="Enter Event Description or Agenda"
              value={formData.description}
              onChange={handleChange}
              onBlur={handleBlur}
              className={inputClass("description")}
              maxLength={1000}
            ></textarea>
            {showError("description") && <span className="error-text">{errors.description}</span>}
            <small className="char-count">{formData.description.length}/1000</small>
          </div>

          <div className="event-btn-group">
            <button type="submit" className="event-submit-btn" disabled={submitting}>
              {submitting ? "Submitting..." : "Submit"}
            </button>
            <button type="button" className="event-reset-btn" onClick={handleReset}>
              Reset
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EventsForm;